<script setup lang="ts">
  import { useStorage, useDebounceFn } from '@vueuse/core'
  import { cva } from 'class-variance-authority'
  import { useOffersDataLayer } from '@/composables/data-layers/useOffersDataLayer'
  import type { IHeaderOffer } from '@/components/main-header/types'

  const { locale } = useI18n()

  const isPopoverOpen = ref(false)

  const localOfferStorage = useStorage<string[]>('localOfferStorage', [])

  const offerIds = ref<string[]>([])
  const offers = ref<IHeaderOffer[]>([])
  const { data: offerList, error } = await useFetch('/api/storyblok/offers-list', {
    params: {
      language: locale.value
    }
  })

  if (!error.value) {
    offerIds.value = Array.isArray(offerList.value) ? offerList.value : []
  }

  const { pushOffersDataLayer } = useOffersDataLayer()

  const updateSelectedOffer = async (offerSlug: string, offerLink: string, secondaryClick: boolean) => {
    if (!Array.isArray(localOfferStorage.value)) {
      localOfferStorage.value = []
    }

    localOfferStorage.value = [...new Set([...localOfferStorage.value, offerSlug])]

    pushOffersDataLayer({
      event_category: 'read_more',
      event_action: offerSlug
    })

    if (secondaryClick) {
      return
    }

    await navigateTo(offerLink, {
      external: true
    })
  }

  const allOffersVisited = computed(() => {
    return offerIds.value.filter((offer) => !localOfferStorage.value.includes(offer)).length === 0
  })

  const offerIndicatorBase = computed(() =>
    cva('relative flex size-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full bg-white', {
      variants: {
        allVisited: {
          true: '',
          false:
            "after:absolute after:right-2.5 after:top-2.5 after:block after:size-2 after:rounded after:bg-bt-red after:content-['']"
        }
      }
    })({
      allVisited: allOffersVisited.value
    })
  )

  const removeOldOffers = () => {
    const activeOffers = offerIds.value.filter((slug) => localOfferStorage.value.includes(slug))

    localOfferStorage.value = [...new Set(activeOffers)]
  }

  removeOldOffers()

  const activeOffersCount = computed(() => {
    const activeOffers = offerIds.value.filter((slug) => !localOfferStorage.value.includes(slug))

    return activeOffers.length
  })

  const sendOffersEvent = async (value?: boolean) => {
    if (!value) return

    pushOffersDataLayer({
      event_category: 'click',
      event_action: activeOffersCount.value
    })
  }

  async function getOffers() {
    const { data: offersData, error: offersError } = await useFetch('/api/storyblok/offers', {
      params: {
        language: locale.value
      }
    })

    if (offersError.value) {
      console.error('Error fetching offers:', offersError.value)
      return []
    }

    offers.value = Array.isArray(offersData.value) ? offersData.value : []
  }

  const debouncedSendOffersEvent = useDebounceFn(sendOffersEvent, 500)

  watch(isPopoverOpen, (value) => {
    if (value) {
      getOffers()
    } else {
      offers.value = []
    }
  })

  onMounted(() => {
    if (activeOffersCount.value > 0) {
      pushOffersDataLayer({
        event_category: 'see',
        event_action: activeOffersCount.value
      })
    }
  })

  const isServer = computed(() => import.meta.server)
</script>

<template>
  <div>
    <UiPopover
      v-model="isPopoverOpen"
      has-overlay
      :label="$t('aria.label.offers')"
      @update:model-value="debouncedSendOffersEvent"
    >
      <template #trigger>
        <ClientOnly>
          <div :class="offerIndicatorBase">
            <Icon name="common/bell" />
          </div>
        </ClientOnly>
        <div
          v-if="isServer"
          class="relative flex size-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full bg-white"
        >
          <Icon name="common/bell" />
        </div>
      </template>
      <template #content>
        <div
          v-if="isPopoverOpen"
          class="fixed flex h-full w-full flex-col overflow-auto bg-white p-5 pt-16 sm:relative sm:h-auto sm:min-w-[22.5rem] sm:rounded sm:pt-5"
        >
          <Offers
            v-for="offer in offers"
            :key="offer.id"
            :slug="offer.slug"
            :title="offer.title"
            :url="offer.link.href"
            class="border-b border-border pb-5 pt-5 last:border-none last:pb-0 sm:first-of-type:pt-0"
            @on-update="updateSelectedOffer"
          />
        </div>
      </template>
      <template #close>
        <button
          class="cursor-pointe relative left-5 top-5 z-10 flex size-6 border border-red-500"
          :aria-label="$t('aria.label.closeOffersMenuButton')"
        >
          <Icon name="common/close" />
        </button>
        <div class="absolute left-0 top-0 h-16 w-full border-b bg-white" />
      </template>
    </UiPopover>
    <UiDarkOverlay
      v-if="isPopoverOpen"
      class="inset-0"
    />
  </div>
</template>
